import { post } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { ALERT_QUERY_KEYS } from 'app/modules/alerts/queries/keys';
import { GetEntitiesResponse } from 'app/modules/entities/responses';
import { RetrieveAlertEntitiesPayload } from 'app/modules/alerts/requests';
import { useSelector } from 'react-redux';
import { selectHasReadAlertsPermission } from 'app/modules/session/selectors';
import { selectDLEnabledForEntityList } from 'app/shared/featureFlags/selectors';
import { GetEntitiesCachedPayload } from 'app/modules/entities/requests';
import { useGetAlert } from 'app/modules/alerts/queries/useGetAlert';
import { useEffect, useState } from 'react';
import { ColdStorageLoadingStatus } from 'app/shared/utils/fastArchHelpers';

export const useGetAlertEntities = (body: RetrieveAlertEntitiesPayload) => {
  const [prevColdStorageLoadingStatus, setPrevColdStorageLoadingStatus] =
    useState<ColdStorageLoadingStatus | null>(null);
  const useCacheEndpoint = useSelector(selectDLEnabledForEntityList);
  const { coldStorageLoading } = useGetAlert(Number(body.alertId), {
    enabled: useCacheEndpoint,
  });
  const hasReadAlertsPermission = useSelector(selectHasReadAlertsPermission);
  const queryEnabled = hasReadAlertsPermission && body.limit !== 0;

  const query = useQuery({
    queryKey: ALERT_QUERY_KEYS.getAlertEntities(body, useCacheEndpoint),
    queryFn: () => {
      if (useCacheEndpoint) {
        const { alertId, ...rest } = body;
        return post<GetEntitiesResponse>(`/entities/cached/list`, {
          alert_ids: [Number(alertId)],
          ...rest,
        } satisfies GetEntitiesCachedPayload);
      }

      return post<GetEntitiesResponse>(
        `/alerts/${body.alertId}/entities`,
        body,
      );
    },
    meta: { errorMessage: 'Failed to load alert entities.' },
    enabled: queryEnabled,
    staleTime: 1000,
  });

  const { refetch } = query;
  useEffect(() => {
    // Re fetch entities if we were waiting for the entities to load from cold storage
    const wasLoading =
      prevColdStorageLoadingStatus === ColdStorageLoadingStatus.LOADING ||
      prevColdStorageLoadingStatus === ColdStorageLoadingStatus.LOADING_TIMEOUT;
    if (
      queryEnabled &&
      useCacheEndpoint &&
      wasLoading &&
      coldStorageLoading.entity === ColdStorageLoadingStatus.LOADED
    ) {
      refetch();
    }
    setPrevColdStorageLoadingStatus(coldStorageLoading.entity);
  }, [
    coldStorageLoading.entity,
    prevColdStorageLoadingStatus,
    useCacheEndpoint,
    queryEnabled,
    refetch,
  ]);

  return query;
};
